import CloudDoneIcon from '@mui/icons-material/CloudDone';
import {
    alpha,
    Box,
    CircularProgress,
    Tooltip,
    Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';
import {
    ConnectionNotice,
    useConnectionNotice,
} from '~/hooks/useConnectionNotice';

/**
 * Opacity of the colour behind the notice: translucent, so it reads as a
 * wash over the header rather than a solid block.
 */
const TINT_ALPHA = 0.28;

type Presentation = {
    label: string;
    tooltip?: string;
    color: 'warning' | 'success';
};

const PRESENTATIONS: Record<NonNullable<ConnectionNotice>, Presentation> = {
    offline: {
        label: 'connection.offline',
        tooltip: 'connection.offlineTooltip',
        color: 'warning',
    },
    reconnecting: {
        label: 'connection.reconnecting',
        tooltip: 'connection.reconnectingTooltip',
        color: 'warning',
    },
    restored: { label: 'connection.restored', color: 'success' },
};

/**
 * A notice centred in the application header that says when the
 * connection to the server is interrupted: work in progress is waiting,
 * not lost, and the user is not left guessing why nothing happens. It
 * shows nothing while connected, and "Connection restored" for a moment
 * after an interruption ends.
 *
 * Positioned over the header rather than in its flow, and the header is
 * fixed in height, so the notice can never move the content below it —
 * an image shifting under a clinician mid-contour would be a hazard.
 * The band lets pointer events through, so a header control under it
 * stays usable while the notice is up; only the icon and text take the
 * pointer, for the tooltip.
 */
export default function ConnectionStatusIndicator() {
    const intl = useIntl();
    const notice = useConnectionNotice();

    if (notice === null) {
        return null;
    }
    const presentation = PRESENTATIONS[notice];
    return (
        <Box
            role="status"
            data-testid="connection-status"
            sx={(theme) => ({
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                px: 3,
                py: 0.75,
                borderRadius: 1,
                whiteSpace: 'nowrap',
                color: theme.palette.text.primary,
                backgroundColor: alpha(
                    theme.palette[presentation.color].main,
                    TINT_ALPHA
                ),
            })}
        >
            <Tooltip
                title={
                    presentation.tooltip
                        ? intl.formatMessage({ id: presentation.tooltip })
                        : ''
                }
            >
                <Box
                    data-testid="connection-status-message"
                    sx={{
                        pointerEvents: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    {presentation.color === 'success' ? (
                        <CloudDoneIcon fontSize="small" color="success" />
                    ) : (
                        <CircularProgress size={16} color="inherit" />
                    )}
                    <Typography fontSize={14} fontWeight={600} component="span">
                        {intl.formatMessage({ id: presentation.label })}
                    </Typography>
                </Box>
            </Tooltip>
        </Box>
    );
}
